import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { handleRpcError } from "@/lib/rpcErrors";
import { toast } from "sonner";

export const useSovChangeRequests = (projectId?: string) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["sov_change_requests", projectId],
    queryFn: async () => {
      let q = supabase
        .from("sov_change_requests")
        .select("*")
        .order("created_at", { ascending: false });
      if (projectId) q = q.eq("project_id", projectId);
      const { data, error } = await q;
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });
};

export const usePendingSovChanges = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["sov_change_requests_pending"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sov_change_requests")
        .select("*, projects(name)")
        .eq("status", "pending")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });
};

export const useCreateSovChangeRequest = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      companyId: string;
      projectId: string;
      sovLineId: string;
      requestedBy: string;
      fieldChanged: string;
      oldDescription?: string;
      newDescription?: string;
      oldQuantity?: number;
      newQuantity?: number;
      oldLaborUnitPrice?: number;
      newLaborUnitPrice?: number;
      oldMaterialUnitPrice?: number;
      newMaterialUnitPrice?: number;
      oldTotal: number;
      newTotal: number;
      reason: string;
    }) => {
      const { data, error } = await supabase
        .from("sov_change_requests")
        .insert({
          company_id: input.companyId,
          project_id: input.projectId,
          sov_line_id: input.sovLineId,
          requested_by: input.requestedBy,
          field_changed: input.fieldChanged,
          old_description: input.oldDescription,
          new_description: input.newDescription,
          old_quantity: input.oldQuantity,
          new_quantity: input.newQuantity,
          old_labor_unit_price: input.oldLaborUnitPrice,
          new_labor_unit_price: input.newLaborUnitPrice,
          old_material_unit_price: input.oldMaterialUnitPrice,
          new_material_unit_price: input.newMaterialUnitPrice,
          old_total: input.oldTotal,
          new_total: input.newTotal,
          financial_impact: input.newTotal - input.oldTotal,
          reason: input.reason,
        } as any)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["sov_change_requests", vars.projectId] });
      qc.invalidateQueries({ queryKey: ["sov_change_requests_pending"] });
      qc.invalidateQueries({ queryKey: ["approval_counts"] });
    },
  });
};

export const useApproveSovChange = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (requestId: string) => {
      const { error } = await supabase.rpc("approve_sov_change" as any, {
        p_request_id: requestId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("SOV change approved");
      qc.invalidateQueries({ queryKey: ["sov_change_requests"] });
      qc.invalidateQueries({ queryKey: ["sov_change_requests_pending"] });
      qc.invalidateQueries({ queryKey: ["sov_lines"] });
      qc.invalidateQueries({ queryKey: ["contracts"] });
      qc.invalidateQueries({ queryKey: ["approval_counts"] });
    },
    onError: (err) => handleRpcError(err),
  });
};

export const useRejectSovChange = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ requestId, reason }: { requestId: string; reason: string }) => {
      const { error } = await supabase.rpc("reject_sov_change" as any, {
        p_request_id: requestId,
        p_rejection_reason: reason,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("SOV change rejected");
      qc.invalidateQueries({ queryKey: ["sov_change_requests"] });
      qc.invalidateQueries({ queryKey: ["sov_change_requests_pending"] });
      qc.invalidateQueries({ queryKey: ["approval_counts"] });
    },
    onError: (err) => handleRpcError(err),
  });
};
